/**
 * WorkspaceReadService.js
 * 
 * Service for reading workspace data from Firestore.
 */

// Firebase
import { collection, collectionGroup, doc, getDoc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Constants
const WORKSPACES_COLLECTION = collections.workspaces;

const getWorkspaceKey = (workspace) => {
  const key = typeof workspace?.key === 'string' ? workspace.key.trim() : '';
  if (key) return key;

  const id = typeof workspace?.id === 'string' ? workspace.id.trim() : '';
  return id || null;
};

const toWorkspaceRow = (docSnap) => {
  const data = docSnap.data() || {};
  const normalizedKey = String(data?.key || docSnap.id || '').trim() || docSnap.id;

  return {
    id: docSnap.id,
    ...data,
    key: normalizedKey,
  };
};

function formatFsError(error) {
  return {
    code: error?.code || null,
    message: error?.message || String(error || ''),
    name: error?.name || null,
  };
}

function toMillisOrMax(value) {
  if (!value) return Number.POSITIVE_INFINITY;

  if (typeof value?.toMillis === 'function') {
    const millis = value.toMillis();
    return Number.isFinite(millis) ? millis : Number.POSITIVE_INFINITY;
  }

  if (typeof value?.seconds === 'number') {
    const nanos = typeof value?.nanoseconds === 'number' ? value.nanoseconds : 0;
    return (value.seconds * 1000) + Math.floor(nanos / 1e6);
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
  }

  return Number.POSITIVE_INFINITY;
}

function pickFallbackWorkspace(workspaces, userKey) {
  const list = Array.isArray(workspaces) ? workspaces.filter(Boolean) : [];
  if (!list.length) return null;

  return [...list].sort((a, b) => {
    const aIsDefault = a?.isDefault === true ? 0 : 1;
    const bIsDefault = b?.isDefault === true ? 0 : 1;
    if (aIsDefault !== bIsDefault) return aIsDefault - bIsDefault;

    const aOwned = a?.ownerKey === userKey ? 0 : 1;
    const bOwned = b?.ownerKey === userKey ? 0 : 1;
    if (aOwned !== bOwned) return aOwned - bOwned;

    const aCreated = toMillisOrMax(a?.dateCreated);
    const bCreated = toMillisOrMax(b?.dateCreated);
    if (aCreated !== bCreated) return aCreated - bCreated;

    const aTitle = String(a?.title || '').trim();
    const bTitle = String(b?.title || '').trim();
    if (aTitle !== bTitle) return aTitle.localeCompare(bTitle);

    const aKey = getWorkspaceKey(a) || '';
    const bKey = getWorkspaceKey(b) || '';
    return aKey.localeCompare(bKey);
  })[0] || null;
}

export async function getWorkspace(workspaceKey) {
  const cleanedWorkspaceKey = String(workspaceKey || '').trim();
  if (!cleanedWorkspaceKey) return null;

  const docSnap = await getDoc(doc(db, WORKSPACES_COLLECTION, cleanedWorkspaceKey));
  return docSnap.exists() ? toWorkspaceRow(docSnap) : null;
}

export async function getInitialWorkspaceForUser({ userKey, preferredWorkspaceKey }) {
  const cleanedUserKey = String(userKey || '').trim();
  const cleanedPreferredWorkspaceKey = String(preferredWorkspaceKey || '').trim();

  if (!cleanedUserKey) {
    return null;
  }

  if (cleanedPreferredWorkspaceKey) {
    try {
      const preferredWorkspace = await getWorkspace(cleanedPreferredWorkspaceKey);
      if (preferredWorkspace) {
        return preferredWorkspace;
      }
    } catch (error) {
      console.warn('[WorkspaceReadService] getInitialWorkspaceForUser preferred workspace failed:', {
        userKey: cleanedUserKey,
        workspaceKey: cleanedPreferredWorkspaceKey,
        error: formatFsError(error),
      });
    }
  }

  const ownerSnapshot = await getDocs(query(
    collection(db, WORKSPACES_COLLECTION),
    where('ownerKey', '==', cleanedUserKey)
  ));
  const ownedWorkspaces = ownerSnapshot.docs.map(toWorkspaceRow);
  const ownedWorkspace = pickFallbackWorkspace(ownedWorkspaces, cleanedUserKey);
  if (ownedWorkspace) {
    return ownedWorkspace;
  }

  const membershipSnapshot = await getDocs(query(
    collectionGroup(db, 'members'),
    where('userKey', '==', cleanedUserKey)
  ));

  const membershipWorkspaceKeys = Array.from(new Set(
    membershipSnapshot.docs
      .map((memberDoc) => {
        const row = memberDoc.data() || {};
        return String(row.workspaceKey || memberDoc.ref.parent?.parent?.id || '').trim();
      })
      .filter(Boolean)
  )).sort((left, right) => left.localeCompare(right));

  if (!membershipWorkspaceKeys.length) {
    return null;
  }

  return getWorkspace(membershipWorkspaceKeys[0]);
}

/**
 * Subscribes to workspaces for a specific user.
 *
 * @param {string} userKey - The user key to scope workspaces.
 * @param {function} callback - Callback function to handle real-time updates.
 *
 * @returns {function(): void} Unsubscribe function
 */
export function subscribeToWorkspaces(userKey, callback) {

  // Validate callback
  if (typeof callback !== 'function') {
    return () => { };
  }

  // Require auth context from caller; without a userKey we return no workspaces.
  if (!userKey) {
    callback([]);
    return () => { };
  }

  const workspaceMap = new Map();
  const sourceByWorkspace = new Map();
  const memberWorkspaceUnsubs = new Map();

  const emitRows = () => {
    callback(Array.from(workspaceMap.values()));
  };

  const removeSourceForWorkspace = (workspaceKey, source) => {
    const sources = sourceByWorkspace.get(workspaceKey);
    if (!sources) return;

    sources.delete(source);
    if (sources.size === 0) {
      sourceByWorkspace.delete(workspaceKey);
      workspaceMap.delete(workspaceKey);
    }

    emitRows();
  };

  const setWorkspaceForSource = (workspaceKey, source, row) => {
    if (!sourceByWorkspace.has(workspaceKey)) {
      sourceByWorkspace.set(workspaceKey, new Set());
    }

    sourceByWorkspace.get(workspaceKey).add(source);
    workspaceMap.set(workspaceKey, row);
    emitRows();
  };

  const applySnapshot = (source, snapshot) => {
    const nextKeys = new Set();

    snapshot.docs.forEach((docSnap) => {
      const key = docSnap.id;
      nextKeys.add(key);

      if (!sourceByWorkspace.has(key)) {
        sourceByWorkspace.set(key, new Set());
      }
      sourceByWorkspace.get(key).add(source);

      workspaceMap.set(key, toWorkspaceRow(docSnap));
    });

    for (const [key, sources] of sourceByWorkspace.entries()) {
      if (!sources.has(source)) continue;
      if (nextKeys.has(key)) continue;

      sources.delete(source);
      if (sources.size === 0) {
        sourceByWorkspace.delete(key);
        workspaceMap.delete(key);
      }
    }

    emitRows();
  };

  const attachMemberWorkspace = (workspaceKey) => {
    if (!workspaceKey || memberWorkspaceUnsubs.has(workspaceKey)) return;

    const workspaceRef = doc(db, WORKSPACES_COLLECTION, workspaceKey);
    const bootstrapSource = `member-bootstrap:${workspaceKey}`;
    const liveSource = `member-live:${workspaceKey}`;

    getDoc(workspaceRef)
      .then((docSnap) => {
        if (!docSnap.exists()) {
          removeSourceForWorkspace(workspaceKey, bootstrapSource);
          return;
        }

        setWorkspaceForSource(workspaceKey, bootstrapSource, toWorkspaceRow(docSnap));
      })
      .catch(() => {
        removeSourceForWorkspace(workspaceKey, bootstrapSource);
      });

    const unsubscribe = onSnapshot(
      workspaceRef,
      (docSnap) => {
        if (!docSnap.exists()) {
          removeSourceForWorkspace(workspaceKey, liveSource);
          return;
        }

        setWorkspaceForSource(workspaceKey, liveSource, toWorkspaceRow(docSnap));
      },
      (error) => {
        removeSourceForWorkspace(workspaceKey, liveSource);
      }
    );

    memberWorkspaceUnsubs.set(workspaceKey, () => {
      unsubscribe();
      removeSourceForWorkspace(workspaceKey, bootstrapSource);
      removeSourceForWorkspace(workspaceKey, liveSource);
    });
  };

  const detachMemberWorkspace = (workspaceKey) => {
    const unsubscribe = memberWorkspaceUnsubs.get(workspaceKey);
    if (!unsubscribe) return;

    unsubscribe();
    memberWorkspaceUnsubs.delete(workspaceKey);
  };

  const ownersQuery = query(
    collection(db, WORKSPACES_COLLECTION),
    where('ownerKey', '==', userKey)
  );

  const membershipsQuery = query(
    collectionGroup(db, 'members'),
    where('userKey', '==', userKey)
  );

  const unsubOwner = onSnapshot(
    ownersQuery,
    (snapshot) => {
      applySnapshot('ownerKey', snapshot);
    },
    (error) => {
      emitRows();
    }
  );

  const unsubMemberships = onSnapshot(
    membershipsQuery,
    (snapshot) => {
      const nextWorkspaceKeys = new Set();

      snapshot.docs.forEach((memberDoc) => {
        const row = memberDoc.data() || {};
        const workspaceKey = String(
          row.workspaceKey
          || memberDoc.ref.parent?.parent?.id
          || ''
        ).trim();

        if (!workspaceKey) return;
        nextWorkspaceKeys.add(workspaceKey);
      });

      nextWorkspaceKeys.forEach((workspaceKey) => attachMemberWorkspace(workspaceKey));

      Array.from(memberWorkspaceUnsubs.keys())
        .filter((workspaceKey) => !nextWorkspaceKeys.has(workspaceKey))
        .forEach((workspaceKey) => detachMemberWorkspace(workspaceKey));
    },
    (error) => {
      console.error('[WorkspaceReadService] memberships:query-error', {
        userKey,
        error: formatFsError(error),
      });
    }
  );

  return () => {
    unsubOwner();
    unsubMemberships();
    Array.from(memberWorkspaceUnsubs.keys()).forEach((workspaceKey) => detachMemberWorkspace(workspaceKey));
  };
}

/**
 * Subscribes to a single workspace by key.
 *
 * @param {string} workspaceKey - Workspace document id.
 * @param {function} callback - Callback function to handle real-time updates.
 *
 * @returns {function(): void} Unsubscribe function
 */
export function subscribeToWorkspace(workspaceKey, callback) {

  // Validate callback
  if (typeof callback !== 'function') {
    return () => { };
  }

  // Validate workspaceKey
  if (!workspaceKey) {
    callback(null);
    return () => { };
  }

  return onSnapshot(
    doc(db, WORKSPACES_COLLECTION, workspaceKey),
    (docSnap) => {
      const data = docSnap.exists() ? toWorkspaceRow(docSnap) : null;
      callback(data);
    },
    (error) => {
      console.error('[WorkspaceReadService] subscribeToWorkspace error:', error);
      callback(null);
    }
  );
}
